import axios from 'axios';

// BigSky Enrichment API admin (super_admin only): its clients and API keys.
// Proxied by the Data Bodies backend (which holds the admin key):
// /api/admin/enrichment-api/*.
const api = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });
const enc = encodeURIComponent;

export const SCOPES = ['enrich', 'graph:read', 'graph:write', 'admin'] as const;
export type Scope = (typeof SCOPES)[number];
export type ClientStatus = 'active' | 'suspended';

export interface ClientInput {
  name: string;
  status: ClientStatus;
  rate_limit_per_minute: number;
  /** null = every attribute. */
  allowed_attributes: string[] | null;
  scopes: Scope[];
  live_lookup: boolean;
  /** The client's keys may also call the API through its MCP server. */
  mcp_enabled: boolean;
}

export interface EnrichmentClient extends ClientInput {
  id: string;
  active_keys: number;
  created_at: string;
  updated_at: string;
}

export interface ApiKeyRecord {
  id: string;
  key_prefix: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** A freshly issued key: `key` is shown once and never retrievable again. */
export interface NewApiKey { key: string; record: ApiKeyRecord }

const base = '/api/admin/enrichment-api/clients';

export async function listClients(token: string, status?: ClientStatus): Promise<EnrichmentClient[]> {
  return (await api.get(base, { ...auth(token), params: status ? { status } : {} })).data.clients;
}
export async function createClient(
  token: string, input: ClientInput & { create_key?: boolean; key_label?: string },
): Promise<{ client: EnrichmentClient; key?: NewApiKey }> {
  return (await api.post(base, input, auth(token))).data;
}
export async function updateClient(token: string, id: string, patch: Partial<ClientInput>): Promise<EnrichmentClient> {
  return (await api.patch(`${base}/${enc(id)}`, patch, auth(token))).data.client;
}
export async function deleteClient(token: string, id: string): Promise<void> {
  await api.delete(`${base}/${enc(id)}`, auth(token));
}
export async function listKeys(token: string, clientId: string): Promise<ApiKeyRecord[]> {
  return (await api.get(`${base}/${enc(clientId)}/keys`, auth(token))).data.keys;
}
export async function issueKey(token: string, clientId: string, label?: string): Promise<NewApiKey> {
  return (await api.post(`${base}/${enc(clientId)}/keys`, label ? { label } : {}, auth(token))).data;
}
export async function revokeKey(token: string, clientId: string, keyId: string): Promise<ApiKeyRecord> {
  return (await api.delete(`${base}/${enc(clientId)}/keys/${enc(keyId)}`, auth(token))).data.key;
}

export interface UsageCounts {
  requests: number;
  records: number;
  /** Billable: records that matched. */
  matches: number;
  /** Part of `matches` answered by a live provider call (paid). */
  external_matches: number;
}
export interface UsageDay extends UsageCounts { day: string }
export interface ClientUsage extends UsageCounts { client_id: string; client_name: string; days: UsageDay[] }

/** Every client's usage between two dates (YYYY-MM-DD); clients with no usage are absent. */
export async function getUsage(token: string, from: string, to: string): Promise<{ from: string; to: string; clients: ClientUsage[] }> {
  return (await api.get('/api/admin/enrichment-api/usage', { ...auth(token), params: { from, to } })).data;
}
export async function getClientUsage(token: string, clientId: string, from: string, to: string): Promise<ClientUsage> {
  return (await api.get(`${base}/${enc(clientId)}/usage`, { ...auth(token), params: { from, to } })).data;
}

/** The backend answers errors as { error: string }. */
export function errorMessage(e: any, fallback: string): string {
  return e?.response?.data?.error || e?.message || fallback;
}
